// RequestsPage — the operator's inbox for everything customers do in the app.
//
// Every customer action writes a `requests` row (sql/071); this page is the record.
// Three kinds land here:
//   • pending  (mode='request') — needs Accept / Decline / Counter-offer
//   • applied  (mode='instant') — already done, undoable until undo_until
//   • info     (messages, card updates) — read and mark done
// Decisions go through the request-decide edge function, which does the Stripe side
// (nothing here touches money directly) and emails the customer.
import { useEffect, useMemo, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { ACTIVE_THEME } from "../themes";
import { useIsMobile } from "../lib/useIsMobile";

const H = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` });
const HJ = () => ({ ...H(), "Content-Type": "application/json" });

const TYPE_LABEL = {
  add_addon: "Add-on", remove_addon: "Removed an add-on", change_plan: "Plan change",
  pause: "Pause", resume: "Resume early", cancel: "Cancellation", keep_plan: "Kept their plan",
  address: "Address change", contact: "Contact details", message: "Message",
  one_off: "One-time job", card: "Card update",
};
const TYPE_ICON = {
  add_addon: "🧴", remove_addon: "↩️", change_plan: "🗓", pause: "⏸", resume: "▶️",
  cancel: "🚪", keep_plan: "💚", address: "📍", contact: "✏️", message: "✉️",
  one_off: "🧽", card: "💳",
};
const DECLINE_REASONS = [
  ["no_product", "No product yet"],
  ["route_full", "Route is full"],
  ["talk_first", "Talk to customer first"],
  ["other", "Something else"],
];

const fmtDate = (d) =>
  d ? new Date(String(d).length <= 10 ? d + "T12:00:00" : d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
const fmtWhen = (ts) => {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return fmtDate(ts);
};

export function RequestsPage({ requests = [], clients = [], onRefresh, showToast }) {
  const isMobile = useIsMobile();
  const apple = ACTIVE_THEME === "apple-native";
  const [tab, setTab] = useState("open");
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("no_product");
  const [counter, setCounter] = useState("");

  useEffect(() => {
    fetch(`${SUPABASE_URL}/rest/v1/rpc/requests_inbox_stats`, { method: "POST", headers: HJ(), body: "{}" })
      .then((r) => r.json()).then(setStats).catch(() => {});
  }, [requests]);

  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  const lists = useMemo(() => {
    const open = requests.filter((r) => r.status === "pending");
    const recent = requests.filter((r) => r.status !== "pending").slice(0, 60);
    return { open, recent };
  }, [requests]);
  const shown = tab === "open" ? lists.open : lists.recent;
  const sel = shown.find((r) => r.id === selId) || null;

  useEffect(() => {
    if (sel) { setNote(""); setReason("no_product"); setCounter(sel.payload?.preferred_date || ""); }
  }, [selId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function open(r) {
    setSelId(r.id);
    if (!r.seen_at) {
      fetch(`${SUPABASE_URL}/rest/v1/rpc/request_mark_seen`, { method: "POST", headers: HJ(), body: JSON.stringify({ p_id: r.id }) })
        .then(() => onRefresh && onRefresh()).catch(() => {});
    }
  }

  async function decide(decision) {
    if (!sel) return;
    setBusy(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/request-decide`, {
        method: "POST", headers: HJ(),
        body: JSON.stringify({
          request_id: sel.id, decision,
          note: note.trim() || null,
          reason: decision === "decline" ? reason : null,
          counter_date: decision === "counter" || (decision === "accept" && counter) ? counter || null : null,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || "That didn't go through.");
      showToast && showToast(d.customer_message || "Saved.", "success");
      setSelId(null);
      onRefresh && onRefresh();
    } catch (e) {
      showToast && showToast(e.message, "error");
    } finally {
      setBusy(false);
    }
  }

  const Row = ({ r }) => {
    const c = clientById[r.client_id];
    const unseen = !r.seen_at;
    return (
      <div
        className="alist-row"
        onClick={() => open(r)}
        style={{
          cursor: "pointer",
          background: selId === r.id ? "var(--accent-weak)" : undefined,
          borderLeft: selId === r.id ? "2px solid var(--accent)" : "2px solid transparent",
        }}
      >
        <div style={{ fontSize: 18, width: 26, textAlign: "center" }} aria-hidden>{TYPE_ICON[r.type] || "•"}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: unseen ? 600 : 500, display: "flex", alignItems: "center", gap: 6 }}>
            {c?.name || "Customer"}
            {unseen ? <span style={{ width: 7, height: 7, borderRadius: 99, background: "var(--accent)" }} /> : null}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r.summary}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 11.5, color: "var(--text3)" }}>{fmtWhen(r.created_at)}</div>
          <StatusPill r={r} />
        </div>
      </div>
    );
  };

  const detail = sel ? (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div>
          <div style={{ fontSize: 12.5, color: "var(--text2)", fontWeight: 600 }}>
            {TYPE_ICON[sel.type]} {TYPE_LABEL[sel.type] || sel.type}
          </div>
          <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: "-.01em", marginTop: 4 }}>
            {clientById[sel.client_id]?.name || "Customer"}
          </div>
        </div>
        <button className="close-btn" onClick={() => setSelId(null)} aria-label="Close">✕</button>
      </div>

      <p style={{ margin: "12px 0 0", fontSize: 15 }}>{sel.summary}</p>
      {sel.body ? (
        <blockquote style={{ margin: "10px 0 0", padding: "10px 12px", background: "var(--bg)", borderRadius: "var(--r-sm)", fontSize: 15 }}>
          {sel.body}
        </blockquote>
      ) : null}

      <div style={{ marginTop: 12, fontSize: 13.5, color: "var(--text2)", display: "grid", gap: 4 }}>
        <div>Sent {fmtWhen(sel.created_at)}</div>
        {sel.effective_on ? <div>Effective {fmtDate(sel.effective_on)}</div> : null}
        {sel.expires_at && sel.status === "pending" ? <div>Expires {fmtDate(sel.expires_at)}</div> : null}
        {sel.undo_until && sel.status === "applied" ? <div>Undoable until {fmtDate(sel.undo_until)}</div> : null}
        {sel.payload?.price ? (
          <div>${Number(sel.payload.price).toFixed(2)}{sel.type === "one_off" ? " — the price they were quoted" : "/mo"}</div>
        ) : null}
        {sel.payload?.simulated ? <div style={{ color: "var(--text3)" }}>Test service — no charge was made.</div> : null}
      </div>

      {sel.status === "pending" || sel.status === "applied" ? (
        <>
          <label style={{ display: "block", marginTop: 14 }}>
            <span style={{ fontSize: 12.5, color: "var(--text2)" }}>A note for the customer (optional)</span>
            <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300}
                   placeholder="Sent with your answer" style={{ width: "100%", marginTop: 4 }} />
          </label>

          {sel.status === "pending" ? (
            <>
              {(sel.type === "one_off" || sel.type === "add_addon") ? (
                <label style={{ display: "block", marginTop: 10 }}>
                  <span style={{ fontSize: 12.5, color: "var(--text2)" }}>
                    {sel.type === "one_off" ? "Day you'll do it" : "Start date (optional)"}
                  </span>
                  <input className="input" type="date" value={counter} onChange={(e) => setCounter(e.target.value)}
                         style={{ width: "100%", marginTop: 4 }} />
                </label>
              ) : null}

              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                <button className="btn" disabled={busy} onClick={() => decide("accept")}>Accept</button>
                {sel.type === "one_off" || sel.type === "add_addon" ? (
                  <button className="btn btn-ghost" disabled={busy || !counter} onClick={() => decide("counter")}>
                    Offer {counter ? fmtDate(counter) : "another day"}
                  </button>
                ) : null}
                <button className="btn btn-ghost" disabled={busy} onClick={() => decide(sel.mode === "info" ? "done" : "decline")}>
                  {sel.mode === "info" ? "Mark done" : "Decline"}
                </button>
              </div>

              {sel.mode !== "info" ? (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12.5, color: "var(--text2)", marginBottom: 6 }}>If you decline, why?</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {DECLINE_REASONS.map(([k, l]) => (
                      <button key={k} onClick={() => setReason(k)}
                              className={`badge ${reason === k ? "badge-blue" : "badge-gray"}`}
                              style={{ border: "none", cursor: "pointer" }}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            // Undo only exists for the two types request-decide can actually reverse, and
            // only inside the window before the change is invoiced. Anything else would
            // mark the row "undone" while the change stayed live in Stripe.
            (() => {
              const undoable = ["add_addon", "remove_addon"].includes(sel.type);
              const open = sel.undo_until && new Date(sel.undo_until) > new Date();
              if (undoable && open) {
                return (
                  <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                    <button className="btn btn-ghost" disabled={busy} onClick={() => decide("undo")}>Undo this change</button>
                  </div>
                );
              }
              return (
                <div style={{ marginTop: 14, fontSize: 14, color: "var(--text2)" }}>
                  {undoable
                    ? "The undo window has closed — this has been invoiced. Change it from the client's plan instead."
                    : "Applied. This one can't be undone from the inbox."}
                </div>
              );
            })()
          )}
        </>
      ) : (
        <div style={{ marginTop: 14, fontSize: 14, color: "var(--text2)" }}>
          {sel.status === "accepted" ? "You accepted this." : sel.status === "declined" ? "You declined this." :
           sel.status === "undone" ? "You undid this." : sel.status === "withdrawn" ? "The customer withdrew it." : "Closed."}
          {sel.decision_note ? ` — “${sel.decision_note}”` : ""}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className={apple ? "calm-pane" : ""}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Requests</div>
          <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
            {/* Only claim a response time once there is enough history to mean anything
                (research: >=20 decided requests in 90 days), else it reads as noise. */}
            What your clients did in the app{stats?.decided_90d >= 20 && stats?.median_response_hours != null
              ? ` · you usually answer in about ${Math.round(stats.median_response_hours)}h` : ""}
          </div>
        </div>
        <div className="seg">
          <button className={tab === "open" ? "on" : ""} onClick={() => { setTab("open"); setSelId(null); }}>
            Open{lists.open.length ? ` (${lists.open.length})` : ""}
          </button>
          <button className={tab === "recent" ? "on" : ""} onClick={() => { setTab("recent"); setSelId(null); }}>Recent</button>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="card" style={{ padding: 28, textAlign: "center", color: "var(--text2)" }}>
          {tab === "open"
            ? "Nothing waiting on you. Anything a client does in the app shows up here."
            : "Nothing decided yet."}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div className="card" style={{ flex: "1 1 440px", padding: 0, overflow: "hidden", minWidth: 0 }}>
            {shown.map((r) => <Row key={r.id} r={r} />)}
          </div>
          {!isMobile && sel ? <div style={{ flex: "0 0 380px", position: "sticky", top: 76 }}>{detail}</div> : null}
        </div>
      )}

      {isMobile && sel ? (
        <>
          <div className="modal-overlay" onClick={() => setSelId(null)} />
          <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 60, maxHeight: "85dvh", overflowY: "auto" }}>
            {detail}
          </div>
        </>
      ) : null}
    </div>
  );
}

function StatusPill({ r }) {
  const map = {
    pending: ["badge-gold", "Waiting"],
    applied: ["badge-blue", "Applied"],
    accepted: ["badge-green", "Accepted"],
    declined: ["badge-gray", "Declined"],
    undone: ["badge-gray", "Undone"],
    withdrawn: ["badge-gray", "Withdrawn"],
    expired: ["badge-gray", "Expired"],
  };
  const [cls, label] = map[r.status] || ["badge-gray", r.status];
  return <span className={`badge ${cls}`} style={{ marginTop: 3 }}>{label}</span>;
}
